// EmployeeManager.js - VERSION CORRIGÉE AVEC LOCALISATIONS
import React, { useState, useEffect } from "react";
import axios from "axios";

function EmployeeManager() {
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    matricule: '',
    name: '',
    department: '',
    position: '',
    baseSalary: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');

  // 🔥 NOUVEAU: Liste des localisations/départements standardisés
  const LOCATIONS = [
    { value: 'Spintoss', label: '🏢 Spintoss' },
    { value: 'Twin Park', label: '🏬 Twin Park' },
    { value: 'Wellness', label: '💊 Wellness' },
    { value: 'Siège', label: '🏛️ Siège' },
    { value: 'Jump Zone', label: '🏬 Jump Zone' },
    { value: 'E-Space', label: '🔧 E-Space' },
    { value: 'BAGNGO', label: '🔧 BAGNGO' }
  ];

  const loadEmployees = async () => {
    try {
      const res = await axios.get("/api/employees");
      setEmployees(res.data.employees);
    } catch (error) {
      console.error("Erreur chargement employés:", error);
      setMessage("❌ Erreur lors du chargement des employés");
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setErrorDetails('');

    // Validation côté client
    if (!formData.matricule.trim() || !formData.name.trim() || !formData.baseSalary) {
      setMessage("❌ Veuillez remplir tous les champs obligatoires");
      setLoading(false);
      return;
    }

    if (parseFloat(formData.baseSalary) <= 0) {
      setMessage("❌ Le salaire doit être supérieur à 0");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        baseSalary: parseFloat(formData.baseSalary),
        matricule: formData.matricule.trim().toUpperCase(),
        name: formData.name.trim()
      };

      console.log("📨 Envoi des données:", payload);

      const res = await axios.post(
        "/api/employees",
        payload,
        {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      setMessage(`✅ ${res.data.message}`);
      setFormData({ 
        matricule: '', 
        name: '', 
        department: '', 
        position: '', 
        baseSalary: '' 
      });
      
      // Recharger après un petit délai
      setTimeout(() => {
        loadEmployees();
      }, 1000);

    } catch (error) {
      console.error("❌ Erreur complète:", error);
      
      if (error.response) {
        const serverError = error.response.data;
        setMessage(`❌ ${serverError.message || "Erreur serveur"}`);
        setErrorDetails(serverError.error || serverError.errors || '');
      } else if (error.request) {
        setMessage("❌ Aucune réponse du serveur. Vérifiez que le serveur est démarré.");
      } else {
        setMessage(`❌ Erreur: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // 🔥 NOUVEAU: Statistiques par localisation
  const getStatsByLocation = () => {
    const stats = {};
    LOCATIONS.forEach(location => {
      stats[location.value] = {
        count: employees.filter(emp => emp.department === location.value).length,
        totalSalary: employees
          .filter(emp => emp.department === location.value)
          .reduce((sum, emp) => sum + (emp.baseSalary || 0), 0)
      };
    });
    return stats;
  };

  const stats = getStatsByLocation();

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <h2 style={{ color: "#2c3e50", marginBottom: "2rem" }}>
        👥 Gestion des Employés et Salaires
      </h2>

      {/* 🔥 NOUVEAU: Statistiques rapides */}
      <div style={{ 
        backgroundColor: "#e7f3ff", 
        padding: "1.5rem", 
        borderRadius: "8px",
        marginBottom: "2rem",
        border: "1px solid #b3d9ff"
      }}>
        <h3 style={{ marginBottom: "1rem", color: "#0056b3" }}>📊 Répartition des Employés</h3>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
          gap: "1rem" 
        }}>
          {LOCATIONS.map(location => (
            <div key={location.value} style={{
              backgroundColor: "white",
              padding: "1rem",
              borderRadius: "6px",
              borderLeft: `4px solid ${
                location.value === 'Spintoss' ? '#03510aff' :
                location.value === 'Twin Park' ? '#e90707ff' :
                location.value === 'Wellness' ? '#3407ffff' :
                location.value === 'E-Space' ? '#0f036eff' :
                location.value === 'Jump Zone' ? '#6a696bff' :
                location.value === 'BAGNGO' ? '#bcad02ff' :
                location.value === 'Siège' ? '#dc3545' : '#6c757d'
              }`
            }}>
              <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
                {location.label}
              </div>
              <div style={{ fontSize: "0.9em", color: "#6c757d" }}>
                {stats[location.value]?.count || 0} employé(s)
              </div>
              {stats[location.value]?.count > 0 && (
                <div style={{ fontSize: "0.8em", color: "#28a745", marginTop: "0.25rem" }}>
                  Moyenne: {(stats[location.value].totalSalary / stats[location.value].count).toFixed(2)} DT
                </div>
              )}
            </div>
          ))}
          <div style={{
            backgroundColor: "#f8f9fa",
            padding: "1rem",
            borderRadius: "6px",
            border: "2px dashed #dee2e6"
          }}>
            <div style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
              📈 Total
            </div>
            <div style={{ fontSize: "0.9em", color: "#6c757d" }}>
              {employees.length} employé(s)
            </div>
            <div style={{ fontSize: "0.8em", color: "#007bff", marginTop: "0.25rem" }}>
              Masse salariale: {employees.reduce((sum, emp) => sum + (emp.baseSalary || 0), 0).toFixed(2)} DT
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire d'ajout */}
      <div style={{ 
        backgroundColor: "#f8f9fa", 
        padding: "2rem", 
        borderRadius: "8px",
        marginBottom: "2rem",
        border: "1px solid #e9ecef"
      }}>
        <h3>➕ Ajouter/Mettre à jour un Employé</h3>
        
        {message && (
          <div style={{ 
            padding: "1rem", 
            backgroundColor: message.includes("✅") ? "#d4edda" : "#f8d7da",
            color: message.includes("✅") ? "#155724" : "#721c24",
            borderRadius: "4px",
            marginBottom: "1rem"
          }}>
            <div><strong>{message}</strong></div>
            {errorDetails && (
              <div style={{ marginTop: "0.5rem", fontSize: "0.9em" }}>
                Détails: {JSON.stringify(errorDetails)}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                Matricule *
              </label>
              <input
                type="text"
                name="matricule"
                value={formData.matricule}
                onChange={handleChange}
                required
                style={{ width: "100%", padding: "0.5rem", border: "1px solid #ced4da", borderRadius: "4px" }}
                placeholder="EMP001"
              />
            </div>
            
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                Nom complet *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{ width: "100%", padding: "0.5rem", border: "1px solid #ced4da", borderRadius: "4px" }}
                placeholder="Jean Dupont"
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                Localisation/Département *
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
                style={{ width: "100%", padding: "0.5rem", border: "1px solid #ced4da", borderRadius: "4px" }}
              >
                <option value="">Choisir une localisation</option>
                {LOCATIONS.map(location => (
                  <option key={location.value} value={location.value}>
                    {location.label}
                  </option>
                ))}
              </select>
              <small style={{ color: "#6c757d", display: "block", marginTop: "0.25rem" }}>
                ⚠️ Important: Détermine les règles de calcul de salaire
              </small>
            </div>
            
            <div>
              <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
                Poste
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                style={{ width: "100%", padding: "0.5rem", border: "1px solid #ced4da", borderRadius: "4px" }}
                placeholder="Développeur, Caissier, Manager..."
              />
            </div>
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
              Salaire de Base Mensuel (DT) *
            </label>
            <input
              type="number"
              name="baseSalary"
              value={formData.baseSalary}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              style={{ width: "100%", padding: "0.5rem", border: "1px solid #ced4da", borderRadius: "4px" }}
              placeholder="1500.00"
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem" }}>
              <small style={{ color: "#6c757d" }}>
                Taux horaire: {formData.baseSalary ? (parseFloat(formData.baseSalary) / 208).toFixed(2) : '0.00'} DT/h
              </small>
              {formData.department && (
                <small style={{ 
                  color: "#28a745", 
                  fontWeight: "bold",
                  backgroundColor: "#d4edda",
                  padding: "0.25rem 0.5rem",
                  borderRadius: "4px"
                }}>
                  📍 {formData.department}
                </small>
              )}
            </div>
          </div>

          <button 
            type="submit"
            disabled={loading}
            style={{
              padding: "0.75rem 2rem",
              backgroundColor: loading ? "#6c757d" : "#28a745",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "1rem",
              width: "100%"
            }}
          >
            {loading ? "⏳ Enregistrement..." : "💾 Enregistrer l'Employé"}
          </button>
        </form>
      </div>

      {/* Liste des employés */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3>📋 Liste des Employés ({employees.length})</h3>
          <div style={{ display: "flex", gap: "0.5rem", fontSize: "0.9em" }}>
            <span style={{ 
              padding: "0.25rem 0.5rem", 
              backgroundColor: "#e7f3ff", 
              borderRadius: "4px",
              color: "#007bff"
            }}>
              🏢 Spintoss: {stats.Spintoss?.count || 0}
            </span>
            <span style={{ 
              padding: "0.25rem 0.5rem", 
              backgroundColor: "#d4edda", 
              borderRadius: "4px",
              color: "#08611dff"
            }}>
              🏬 Twin Park: {stats['Twin Park']?.count || 0}
            </span>
            <span style={{ 
              padding: "0.25rem 0.5rem", 
              backgroundColor: "#e7f3ff", 
              borderRadius: "4px",
              color: "#cc0808ff"
            }}>
              🏬 Jump Zone: {stats['Jump Zone']?.count || 0}
            </span>
            <span style={{ 
              padding: "0.25rem 0.5rem", 
              backgroundColor: "#e7f3ff", 
              borderRadius: "4px",
              color: "#585858ff"
            }}>
              🏬 E-Space: {stats['E-Space']?.count || 0}
            </span>
            <span style={{ 
              padding: "0.25rem 0.5rem", 
              backgroundColor: "#fff3cd", 
              borderRadius: "4px",
              color: "#0c076fff"
            }}>

              💊 Wellness: {stats.wellness?.count || 0}
            </span>
             <span style={{ 
              padding: "0.25rem 0.5rem", 
              backgroundColor: "#fff3cd", 
              borderRadius: "4px",
              color: "#12079fff"
            }}>

              💊 BAGNGO: {stats.BAGNGO?.count || 0}
            </span>
          </div>
        </div>
        
        {employees.length === 0 ? (
          <p style={{ textAlign: "center", color: "#6c757d", padding: "2rem" }}>
            Aucun employé enregistré
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse",
              backgroundColor: "white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}>
              <thead style={{ backgroundColor: "#343a40", color: "white" }}>
                <tr>
                  <th style={{ padding: "12px", textAlign: "left" }}>Matricule</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Nom</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Localisation</th>
                  <th style={{ padding: "12px", textAlign: "left" }}>Poste</th>
                  <th style={{ padding: "12px", textAlign: "center" }}>Salaire Base</th>
                  <th style={{ padding: "12px", textAlign: "center" }}>Taux Horaire</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #dee2e6" }}>
                    <td style={{ padding: "12px", fontWeight: "bold" }}>{emp.matricule}</td>
                    <td style={{ padding: "12px" }}>{emp.name}</td>
                    <td style={{ padding: "12px" }}>
                      <span style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "0.8em",
                        fontWeight: "bold",
                        backgroundColor: 
                          emp.department === 'Spintoss' ? '#e7f3ff' :
                          emp.department === 'Twin Park' ? '#d4edda' :
                          emp.department === 'Wellness' ? '#fff3cd' :
                          emp.department === 'Siège' ? '#f8d7da' : '#f8f9fa',
                        color:
                          emp.department === 'Spintoss' ? '#0056b3' :
                          emp.department === 'Twin Park' ? '#035516ff' :
                          emp.department === 'Wellness' ? '#856404' :
                          emp.department === 'Siège' ? '#721c24' : '#6c757d'
                      }}>
                        {emp.department || 'Non spécifié'}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>{emp.position || '-'}</td>
                    <td style={{ padding: "12px", textAlign: "center", fontWeight: "bold" }}>
                      {emp.baseSalary?.toFixed(2)} DT
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {emp.hourlyRate?.toFixed(2)} DT/h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmployeeManager;
